import { isObject } from '@iconik-io/core/src/is_object';

import { deepMergePartial } from '../../utils/deep_merge_partials';
import type { SyncAtom } from '../sync_atom';
import type { SyncMap } from '../sync_map';
import type { SyncStore } from '../sync_store';
import { validateSyncData } from '../validators';
import type { Message, SyncAction, SyncData, SyncMessage } from './sync_client';

/**
 * Manages sync data batching and sending
 */
export class SyncDataManager {
  #pendingSyncData: SyncData[] = [];
  #batchTimeout: ReturnType<typeof setTimeout> | undefined;
  #currentBatchId: string | undefined;
  #unconfirmedStorage: Map<string, Set<SyncStore>> = new Map();
  #unknownDataStorage: Map<string, SyncData> = new Map();
  #messageCounter = 0;
  isSyncing = false;
  syncStores: Map<string, SyncStore> = new Map();
  #sendMessage: (message: Message) => void;

  constructor(sendMessage: (message: Message) => void) {
    this.#sendMessage = sendMessage;
  }

  queueSyncData(store: SyncAtom | SyncMap, value: unknown, key?: string): void {
    if (!this.#currentBatchId) {
      this.#currentBatchId = `id-${this.#messageCounter++}`;
    }

    let storesForBatch = this.#unconfirmedStorage.get(this.#currentBatchId);
    if (!storesForBatch) {
      storesForBatch = new Set();
      this.#unconfirmedStorage.set(this.#currentBatchId, storesForBatch);
    }
    storesForBatch.add(store);

    store.setUnconfirmed(this.#currentBatchId, key);

    let action: SyncAction;
    if (key !== undefined) {
      action = value !== undefined ? 'SET_KEY' : 'UNSET_KEY';
    } else {
      action = value !== undefined ? 'SET' : 'UNSET';
    }

    const syncData: SyncData = {
      atom: store.name,
      action,
      last_set_timestamp: store.lastSetTimestamp,
    };

    if (value !== undefined) {
      syncData.value = value;
    }

    // Only include key if it's defined (for syncMap operations)
    if (key !== undefined) {
      syncData.key = key;
    }

    if (import.meta.env.DEV && !validateSyncData(syncData)) {
      // biome-ignore lint/suspicious/noConsole: validation error
      console.error(
        `[Sync Send Validation Failed] Invalid data for atom '${store.name}':`,
        syncData,
      );
      return;
    }

    this.#pendingSyncData.push(syncData);

    if (this.#batchTimeout) {
      clearTimeout(this.#batchTimeout);
    }

    // Send sync data in batches of 0ms (One event loop tick) to make dependent changes sync at the same time.
    this.#batchTimeout = setTimeout(() => this.#sendBatch(), 0);
  }

  flush(): void {
    clearTimeout(this.#batchTimeout);
    this.#batchTimeout = undefined;
    this.#sendBatch();
  }

  #sendBatch(): void {
    if (this.#pendingSyncData.length === 0) {
      return;
    }

    const message: SyncMessage = {
      command: 'SYNC',
      command_id: this.#currentBatchId,
      data: this.#pendingSyncData,
    };

    this.#sendMessage(message);
    this.#pendingSyncData = [];
    this.#currentBatchId = undefined;
    this.#batchTimeout = undefined;
  }

  handleSyncConfirmation(commandId: string | undefined): void {
    if (!commandId) {
      // biome-ignore lint/suspicious/noConsole: debug
      console.error('No command_id for sync confirmation');
      return;
    }
    const stores = this.#unconfirmedStorage.get(commandId);
    if (stores) {
      // Process confirmation for each store in the batch
      for (const store of stores) {
        store.setConfirmed(commandId);
      }

      // Remove the entire batch from unconfirmed storage
      this.#unconfirmedStorage.delete(commandId);
    }
  }

  setIsSyncing(value: boolean): void {
    this.isSyncing = value;
  }

  storeUnknownData(data: SyncData): void {
    const {
      atom,
      action,
      value,
      key,
      last_set_timestamp: lastSetTimestamp,
    } = data;

    if (key !== undefined) {
      const currentValue = this.#unknownDataStorage.get(atom);
      const currentValueObject = isObject(currentValue?.value)
        ? currentValue.value
        : {};

      const [newValue] = deepMergePartial(currentValueObject, {
        [key]: value,
      });
      this.#unknownDataStorage.set(atom, {
        atom,
        action: 'SET',
        value: newValue,
        last_set_timestamp: lastSetTimestamp,
      });
      return;
    }

    // Overwrite the full unknown data if it doesn't have a key
    this.#unknownDataStorage.set(atom, {
      atom,
      action,
      value,
      last_set_timestamp: lastSetTimestamp,
    });
  }

  getBootstrapData(): SyncData[] {
    const bootstrapData: SyncData[] = [];
    for (const [name, store] of this.syncStores) {
      const bootstrapValue = store.bootstrapValue();
      const bootstrapEntry: SyncData = {
        atom: name,
        action: bootstrapValue === undefined ? 'UNSET' : 'SET',
        last_set_timestamp: store.lastSetTimestamp,
      };
      if (bootstrapValue !== undefined) {
        bootstrapEntry.value = bootstrapValue;
      }
      bootstrapData.push(bootstrapEntry);
    }
    for (const [name, data] of this.#unknownDataStorage) {
      const inferredAction =
        data.action ?? (data.value === undefined ? 'UNSET' : 'SET');
      const bootstrapEntry: SyncData = {
        atom: name,
        action: inferredAction,
        last_set_timestamp: data.last_set_timestamp,
      };
      if (data.value !== undefined) {
        bootstrapEntry.value = data.value;
      }
      bootstrapData.push(bootstrapEntry);
    }
    return bootstrapData;
  }

  clear(): void {
    for (const [_, store] of this.syncStores) {
      store.unconfirmed = undefined;
      store.setLastSetTimestamp(0);
    }
    this.#unconfirmedStorage.clear();
    this.#unknownDataStorage.clear();
    this.#pendingSyncData = [];
    this.#currentBatchId = undefined;
    this.#messageCounter = 0;
    this.isSyncing = false;
  }
}
